import { UIModelType } from '@src/slide/domain/ui-model/element-modeles/constant';
import ElementBaseModel from './element-base-model';
import { transaction } from '@src/slide/domain/slide-app';
import { IShapeBaseModel } from '@src/slide/domain/ui-model/element-modeles/Interface/shape-model.interface';
import { IShapeModelProps } from '@src/slide/domain/ui-model/element-modeles/Interface/model-props/shape-model-props.interface';
import {
    ICTTextBody, STPlaceholderType, IMasterDataModel, IShapeData, DeltaOf,
    IShapeDataModel, ShapeDataModel, MasterDataModel,
} from '@tencent/slide-sdk-model';
import { cloneDeep } from '@tencent/slide-sdk-model/src/utils/clone-deep';
import { getChangedPart, deleteNullAttr } from '@src/slide/infrastructure/module/independent/object/object-path';
import _get from 'lodash/get';
import { ICTTextParagraphProperties } from '@tencent/slide-sdk-pb-model/src/ultrabuf/ultrabuf.types';
import { TextConverter } from '@src/slide/domain/ui-model/converter/node-property/module-property/text/text-converter';
import { isEmptyObj } from '@src/slide/infrastructure/module/independent/assert';
import { PropertyModificationType, linkageDecoratorFactoryForShapeModel } from '../../linkage-change-helper';
import { changeArrAppendToSetOrModify } from '@src/slide/infrastructure/module/independent/object/arr-append-to-set-or-modify';

const PropsNeedRetain = ['lang'];
class ShapeBaseModel extends ElementBaseModel implements IShapeBaseModel {
    /**
     * 3.0sdk更新文本
     * @param uiSdk
     * @param txBody
     */
    public static sdkV3UpdateParagraphList(uiSdk: ShapeDataModel, txBody: ICTTextBody): void {
        if (!txBody) {
            return;
        }
        // 包含无用的set，剔除未变的属性 ( ?? uiSdk是为了兼容单元格 TableCellProps)
        const changedTxBody = getChangedPart((uiSdk.store ?? uiSdk).data.txBody, txBody);
        if (!Object.keys(changedTxBody || {}).length) {
            return;
        }
        uiSdk.api.transaction(() => {
            if (Object.keys(changedTxBody?.bodyPr || {}).length) {
                changeArrAppendToSetOrModify(changedTxBody?.bodyPr ?? {});
                uiSdk.api?.updateTextBodyProperties?.({ bodyPr: changedTxBody?.bodyPr });
            }
            if (changedTxBody?.p) {
                deleteNullAttr(changedTxBody.p);
                uiSdk.api?.updateParagraphList?.(changedTxBody.p);
            }
        });
    }

    public sdkData: ShapeDataModel | null = null;
    declare public props: IShapeModelProps;

    public constructor(props?: {}) {
        super(props);
        this.type = UIModelType.SHAPE;
    }


    @linkageDecoratorFactoryForShapeModel([['paragraphList', PropertyModificationType.update], ['textBodyStyle', PropertyModificationType.update]])
    public updateParagraphList(paragraphList, textBodyStyle): void {
        if (!this.sdkData) return;
        const sdkData = this.prepareSDKParagraphListData(paragraphList, textBodyStyle);
        ShapeBaseModel.sdkV3UpdateParagraphList(this.sdkData as unknown as ShapeDataModel, sdkData?.txBody);
    }

    @linkageDecoratorFactoryForShapeModel([['textBodyStyle', PropertyModificationType.update]])
    public updateTextBodyStyle(textBodyStyle): void {
        if (isEmptyObj(textBodyStyle)) {
            return;
        }
        if (!this.sdkData) return;
        const sdkData = this.prepareSDKData({ textBodyStyle });
        ShapeBaseModel.sdkV3UpdateParagraphList(this.sdkData as unknown as ShapeDataModel, sdkData?.txBody);
    }

    /**
     * 更新母版/版式的段落时，需要将段落样式中除了语言之外的其他部分移除
     * 段落的样式更新到对应的 styleList 中
     * @param {*} sdkData
     */
    public updateMasterAndLayoutParagraphList(sdkData) {
        sdkData.txBody?.p
            ?.forEach(({ egTextRun }) => egTextRun.forEach((item) => {
                this.deleteExtraProps(item);
            }));
        if (sdkData.txBody?.p?.[0]?.pPr) {
            delete sdkData.txBody.p[0].pPr;
        }
        // 上面把段落属性、文字属性全部移除，只保留文字和文本框属性。 段落试下和文字属性更新在lstStyle里
        ShapeBaseModel.sdkV3UpdateParagraphList(this.sdkData as unknown as ShapeDataModel, sdkData?.txBody);
    }

    /**
     * 更新母版、版式占位符的样式
     * 将 editor 中的样式一层层 merge 到继承样式中
     * master 的 title、ctrTitle、body 比较特殊，会把样式更新到 master 上
     * 其他占位符的样式更新到对应的 textStyleList 中
     * @param paragraphList
     * @param textBodyStyle
     * @param isTextCustom 是否使用自定义的占位符文本：可以将版式中修改的文本反馈到页里
     */
    public updateMasterAndLayoutPlaceHolder(paragraphList, textBodyStyle, isTextCustom) {
        this.updateMasterAndLayoutPlaceHolderV3(paragraphList, textBodyStyle, isTextCustom);
    }

    public prepareSDKParagraphListData(paragraphList, textBodyStyle): any {
        return this.prepareSDKData({ paragraphList, textBodyStyle });
    }

    private getExtendStyleListV3() {
        if (!this.isPlaceholder) return {};
        if (this.isFromMaster) {
            const typeString: string = _get(this, 'placeHolder.type');
            const placeHolderType = STPlaceholderType[typeString];
            const masterUiModel = this.masterRef || { sdkData: null };
            switch (placeHolderType) {
                case STPlaceholderType.title:
                case STPlaceholderType.ctrTitle:
                    return {
                        key: 'titleStyle',
                        value: cloneDeep((masterUiModel.sdkData as unknown as MasterDataModel)
                            ?.store?.data?.txStyles?.titleStyle),
                    };
                case STPlaceholderType.dt:
                case STPlaceholderType.ftr:
                case STPlaceholderType.sldNum:
                    return {
                        key: 'lstStyle',
                        value: cloneDeep((this.sdkData as unknown as ShapeDataModel)
                            ?.store?.data?.txBody?.lstStyle),
                    };
                default:
                    return {
                        key: 'bodyStyle',
                        value: cloneDeep((masterUiModel.sdkData as unknown as MasterDataModel)
                            ?.store?.data?.txStyles?.bodyStyle),
                    };
            }
        }
        if (this.isFromLayout) {
            return {
                key: 'lstStyle',
                value: cloneDeep((this.sdkData as unknown as ShapeDataModel)
                    ?.store?.data?.txBody?.lstStyle),
            };
        }
        return {};
    }

    private paragraphListToLevelMap(sdkData: DeltaOf<IShapeData>): { [key: string]: ICTTextParagraphProperties } {
        if (!sdkData?.txBody?.p?.length) {
            return {};
        }
        const map = {};
        sdkData.txBody.p.forEach((p) => {
            const lvl = p.pPr?.lvl || 0;
            const key = `lvl${lvl + 1}pPr`;
            map[key] = Object.assign({}, map[key], p.pPr, { lvl });
            if (p.egTextRun?.[0]?.r?.rPr) {
                map[key].defRPr = Object.assign({}, p.egTextRun?.[0]?.r?.rPr);
            }
            if (p.egTextRun?.[0]?.fld?.rPr) {
                map[key].defRPr = Object.assign({}, p.egTextRun?.[0]?.fld?.rPr);
            }
        });
        return map;
    }

    private updateMasterAndLayoutPlaceHolderV3(paragraphList, textBodyStyle, isTextCustom) {
        // 因为就是要补充继承属性，所以所有属性都需要保留，强制给所有属性加上raw标签
        TextConverter.addParagraphListRawSignProps(paragraphList, paragraphList);
        const sdkData = this.prepareSDKParagraphListData(paragraphList, textBodyStyle);
        const { key: styleListKey, value: existLvlMap }: any = this.getExtendStyleListV3() || {};
        const newLevelMap = this.paragraphListToLevelMap(sdkData);
        if (existLvlMap && newLevelMap) {
            Object.keys(newLevelMap).forEach((key) => {
                existLvlMap[key] = Object.assign({}, newLevelMap[key]);
            });
        }
        transaction(() => {
            this.updateMasterAndLayoutParagraphList(cloneDeep(sdkData));
            if (this.isFromMaster && (this.isTitleOrCtrTitlePl || this.isBodyPl)) {
                (this.masterRef?.sdkData as unknown as IMasterDataModel)
                    ?.api?.updateTextStyles(styleListKey, existLvlMap);
            } else {
                if (isTextCustom !== undefined) {
                    this.updateCustomPrompt(isTextCustom);
                }
                changeArrAppendToSetOrModify(existLvlMap);
                (this.sdkData as unknown as IShapeDataModel).api?.updateTextStyles(existLvlMap);
            }
        });
    }

    private deleteExtraProps(item) {
        const rPr = item?.fld?.rPr || item?.r?.rPr;
        if (rPr) {
            Object.keys(rPr).forEach((key) => {
                if (PropsNeedRetain.includes(key)) {
                    return;
                }
                delete rPr[key];
            });
        }
    }

    /**
     * 更新参数：hasCustomPrompt - 占位符文案是否使用自定义的版本
     */
    private updateCustomPrompt(isTextCustom: boolean) {
        (this.sdkData as unknown as IShapeDataModel).api.updateProps({
            nvPr: {
                ph: {
                    hasCustomPrompt: isTextCustom ? true : null,
                },
            },
        });
    }
}

export default ShapeBaseModel;
